/**
 * Sharing, through the share sheet where there is one: the result's text, else the clipboard; and
 * a picture of the clearing, as a file.
 */

export type ShareOutcome = "shared" | "copied" | "manual";

export async function shareResult(text: string): Promise<ShareOutcome> {
  try {
    if (typeof navigator.share === "function" && navigator.canShare?.({ text })) {
      await navigator.share({ text });
      return "shared";
    }
  } catch {
    // The player dismissed the sheet, or it failed; fall through to the clipboard.
  }
  try {
    await navigator.clipboard.writeText(text);
    return "copied";
  } catch {
    return "manual";
  }
}

/** Whether the share sheet here takes a picture as a file. */
export function canShareImage(): boolean {
  if (typeof navigator.share !== "function" || typeof navigator.canShare !== "function") {
    return false;
  }
  try {
    return navigator.canShare({ files: [new File([], "picture.png", { type: "image/png" })] });
  } catch {
    return false;
  }
}

/** Shares a picture through the share sheet: shared, or dismissed by the player, or failed. */
export async function shareImage(file: File): Promise<"shared" | "dismissed" | "failed"> {
  try {
    await navigator.share({ files: [file] });
    return "shared";
  } catch (error) {
    return error instanceof DOMException && error.name === "AbortError" ? "dismissed" : "failed";
  }
}
